/*
 * The marketing half of the Procurement destination — campaigns, and the projection that divides
 * by them.
 *
 * The destination has been called "จัดซื้อและการตลาด" since the registry was written, and the
 * rebuilt screen delivered only the first half: nothing under `client/src/console/` called
 * `listCampaigns`, `createCampaign`, `updateCampaign`, `deleteCampaign` or `marketingSummary`, so
 * an organizer in `?ui=v2` could not record what an ad campaign cost, and the ROAS the platform
 * computes for them had nothing to divide by.
 *
 * `actualAdsSpend` is `null` when nobody has filled it in, and that is not zero: zero reads as "we
 * spent and got nothing", which leads to the opposite decision from "we do not know yet"
 * (TICK-111 D5). Everything here preserves that distinction rather than collapsing it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, opsApi } from '../../../api';
import type { OpsCampaignDto, OpsCampaignInput, OpsMarketingSummaryDto } from '../../../api';

export interface MarketingCoverage {
  total: number;
  /** Campaigns whose actual spend somebody has actually entered. */
  reported: number;
  missing: number;
  /**
   * True while some campaigns have an actual figure and others do not.
   *
   * The server divides the event's *whole* paid revenue by the sum of only the campaigns that have
   * a figure, so a half-filled register makes ROAS look better than it is — and neither the number
   * nor the old screen said so. This is the flag that lets the screen say it.
   */
  partial: boolean;
}

export function marketingCoverage(campaigns: readonly OpsCampaignDto[]): MarketingCoverage {
  const total = campaigns.length;
  const reported = campaigns.filter((c) => c.actualAdsSpend !== null).length;
  return { total, reported, missing: total - reported, partial: reported > 0 && reported < total };
}

export interface OpsMarketingState {
  campaigns: OpsCampaignDto[];
  summary: OpsMarketingSummaryDto | null;
  coverage: MarketingCoverage;
  loading: boolean;
  loadError: string;
  error: string;
  /** The campaign id currently being written, or `'new'`. Empty while idle. */
  busy: string;
  /** `true` back means it was written — the form clears only then. */
  create: (input: OpsCampaignInput) => Promise<boolean>;
  /** Records what a campaign actually cost. `null` puts it back to "not filled in". */
  setSpend: (c: OpsCampaignDto, actualAdsSpend: number | null, spendSource: string) => Promise<boolean>;
  remove: (c: OpsCampaignDto) => Promise<boolean>;
  reload: () => Promise<void>;
}

export function useOpsMarketing(token: string, eventSlug?: string): OpsMarketingState {
  const [campaigns, setCampaigns] = useState<OpsCampaignDto[] | null>(null);
  const [summary, setSummary] = useState<OpsMarketingSummaryDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      /*
       * Both together, always. The summary is computed from the campaigns, so fetching one without
       * the other puts two views of the same figures on screen that can disagree — which is exactly
       * what this screen is for reading.
       */
      const [rows, marketing] = await Promise.all([
        opsApi.listCampaigns(token, eventSlug),
        opsApi.marketingSummary(token, eventSlug),
      ]);
      setCampaigns(rows);
      setSummary(marketing);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดข้อมูลการตลาดไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (key: string, fn: () => Promise<unknown>, fallback: string) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
      return false;
    } finally {
      setBusy('');
    }
  }, [load]);

  return {
    campaigns: campaigns ?? [],
    summary,
    coverage: marketingCoverage(campaigns ?? []),
    loading: campaigns === null && !loadError,
    loadError,
    error,
    busy,
    create: (input) => run('new', () => opsApi.createCampaign(token, input, eventSlug), 'สร้างแคมเปญไม่สำเร็จ'),
    setSpend: (c, actualAdsSpend, spendSource) => run(
      c.id,
      () => opsApi.updateCampaign(token, c.id, { actualAdsSpend, spendSource, version: c.version }, eventSlug),
      'บันทึกค่าโฆษณาไม่สำเร็จ',
    ),
    remove: (c) => run(c.id, () => opsApi.deleteCampaign(token, c.id, c.version, eventSlug), 'ลบแคมเปญไม่สำเร็จ'),
    reload: load,
  };
}
